/**
 * Client List View Queries (End User Data Viewing)
 *
 * React Query hooks for displaying client data in list views.
 * Uses Gobodo API via recordStorageService for records.
 * List view configurations still use localStorage (mock) for now.
 *
 * NOTE: This is separate from /services/queries/listViewQueries.ts which is
 * for Object Manager admin configuration.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { persistedClientListViews, persistedClientRecords } from '../mockData/clients';
import { getRecordsForObjectPage } from '../recordStorageService';
import { normalizeRecordForListView } from '../../utils/listViewRecordNormalizer';
import { resolveFieldValue } from '../../utils/fieldValueResolver';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { USE_RDS_FOR_CORE_OBJECTS, isRdsCoreObject } from '../../config/rdsConfig';
import type { ListView, ListViewRecord, ListViewData } from '../../types/listView';

// Mock list views for Clients
const mockClientListViews: ListView[] = [
  {
    id: 'lv_clients_all',
    name: 'All Clients',
    apiName: 'all_clients',
    objectApiName: 'Client',
    description: 'All client records',
    isDefault: true,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Client Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'datetime', isVisible: true, order: 3 },
      { fieldApiName: 'industry', fieldLabel: 'Industry', fieldType: 'text', isVisible: true, order: 4 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 5 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_clients_active',
    name: 'Active Clients',
    apiName: 'active_clients',
    objectApiName: 'Client',
    description: 'Clients with Active status',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Client Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'datetime', isVisible: true, order: 2 },
      { fieldApiName: 'industry', fieldLabel: 'Industry', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 4 },
    ],
    filters: [
      { fieldApiName: 'status', operator: 'equals', value: 'Active' }
    ],
    createdBy: 'Sarah Johnson',
    createdDate: '2024-02-15T10:30:00Z'
  },
  {
    id: 'lv_clients_recent',
    name: 'Recently Created',
    apiName: 'recent_clients',
    objectApiName: 'Client',
    description: 'Clients created in the last 30 days',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Client Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'createdDate', fieldLabel: 'Created Date', fieldType: 'datetime', isVisible: true, order: 1 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 2 },
      { fieldApiName: 'accountOwner', fieldLabel: 'Account Owner', fieldType: 'text', isVisible: true, order: 3 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 4 },
    ],
    sortBy: {
      fieldApiName: 'createdDate',
      direction: 'desc'
    },
    createdBy: 'Michael Chen',
    createdDate: '2024-03-10T14:00:00Z'
  },
  {
    id: 'lv_clients_my',
    name: 'My Clients',
    apiName: 'my_clients',
    objectApiName: 'Client',
    description: 'Clients I own',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Client Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 1 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'datetime', isVisible: true, order: 2 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 3 },
    ],
    filters: [
      { fieldApiName: 'accountOwner', operator: 'equals', value: 'Current User' }
    ],
    createdBy: 'Sarah Johnson',
    createdDate: '2024-04-01T10:00:00Z'
  }
];

// Mock client records
const mockClientRecords: ListViewRecord[] = [
  {
    id: 'client_001',
    name: 'John Smith',
    email: 'john.smith@email.com',
    phone: '(555) 123-4567',
    status: 'Active',
    accountOwner: 'Sarah Johnson',
    industry: 'Technology',
    lastContact: '2025-01-04T10:00:00Z',
    createdDate: '2024-01-15T10:30:00Z'
  },
  {
    id: 'client_002',
    name: 'Acme Corporation',
    email: 'contact@acme.com',
    phone: '(555) 234-5678',
    status: 'Active',
    accountOwner: 'Michael Chen',
    industry: 'Manufacturing',
    lastContact: '2025-01-03T14:20:00Z',
    createdDate: '2024-02-20T09:15:00Z'
  },
  {
    id: 'client_003',
    name: 'Global Industries Inc',
    email: 'info@globalind.com',
    phone: '(555) 345-6789',
    status: 'Inactive',
    accountOwner: 'Sarah Johnson',
    industry: 'Retail',
    lastContact: '2024-11-15T11:00:00Z',
    createdDate: '2023-11-05T08:45:00Z'
  },
  {
    id: 'client_004',
    name: 'Tech Startup LLC',
    email: 'hello@techstartup.io',
    phone: '(555) 456-7890',
    status: 'Active',
    accountOwner: 'David Wilson',
    industry: 'Technology',
    lastContact: '2025-01-02T09:30:00Z',
    createdDate: '2024-03-10T11:00:00Z'
  },
  {
    id: 'client_005',
    name: 'Financial Services Group',
    email: 'contact@finservices.com',
    phone: '(555) 567-8901',
    status: 'Active',
    accountOwner: 'Emily Davis',
    industry: 'Finance',
    lastContact: '2025-01-01T16:45:00Z',
    createdDate: '2023-09-12T13:30:00Z'
  },
  {
    id: 'client_006',
    name: 'Retail Solutions Co',
    email: 'sales@retailsolutions.com',
    phone: '(555) 678-9012',
    status: 'Pending',
    accountOwner: 'Michael Chen',
    industry: 'Retail',
    lastContact: '2024-12-28T10:15:00Z',
    createdDate: '2024-06-15T14:00:00Z'
  },
  {
    id: 'client_007',
    name: 'Healthcare Partners',
    email: 'info@healthcarepartners.com',
    phone: '(555) 789-0123',
    status: 'Active',
    accountOwner: 'Sarah Johnson',
    industry: 'Healthcare',
    lastContact: '2025-01-03T08:15:00Z',
    createdDate: '2024-04-22T10:00:00Z'
  },
  {
    id: 'client_008',
    name: 'Education First',
    email: 'contact@educationfirst.edu',
    phone: '(555) 890-1234',
    status: 'Active',
    accountOwner: 'David Wilson',
    industry: 'Education',
    lastContact: '2024-12-30T13:45:00Z',
    createdDate: '2024-05-18T09:30:00Z'
  },
  {
    id: 'client_009',
    name: 'Construction Co',
    email: 'info@constructionco.com',
    phone: '(555) 901-2345',
    status: 'Active',
    accountOwner: 'Michael Chen',
    industry: 'Construction',
    lastContact: '2025-01-04T14:00:00Z',
    createdDate: '2024-07-10T11:15:00Z'
  },
  {
    id: 'client_010',
    name: 'Legal Associates',
    email: 'contact@legalassociates.com',
    phone: '(555) 012-3456',
    status: 'Active',
    accountOwner: 'Emily Davis',
    industry: 'Legal',
    lastContact: '2025-01-02T16:30:00Z',
    createdDate: '2024-08-05T09:45:00Z'
  }
];

/**
 * Get available list views for clients
 */
export function useClientListViews() {
  return useQuery({
    queryKey: ['clientListViews'],
    queryFn: async () => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));

      return persistedClientListViews.get();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

const DEFAULT_PAGE_SIZE = 25;

/**
 * Get list view data (view configuration + records) with server-side pagination
 */
export function useClientListViewData(
  listViewId: string,
  options?: { page?: number; pageSize?: number }
) {
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const useBackend = shouldUseGobodo() || (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject('Client__c'));

  return useQuery<ListViewData>({
    queryKey: ['clientListViewData', listViewId, page, pageSize],
    queryFn: async () => {
      const allViews = persistedClientListViews.get();
      const listView = allViews.find(lv => lv.id === listViewId);
      if (!listView) throw new Error('List view not found');

      if (useBackend) {
        const { records: pageRecords, totalRecords } = await getRecordsForObjectPage('Client__c', { page, size: pageSize });
        let records: ListViewRecord[] = pageRecords.map((r) =>
          normalizeRecordForListView('Client__c', r) as ListViewRecord
        );

        if (listView.filters?.length) {
          listView.filters.forEach(filter => {
            records = records.filter(record => {
              const fieldValue = resolveFieldValue(record, filter.fieldApiName);
              switch (filter.operator) {
                case 'equals':
                  if (filter.value === 'Current User') return fieldValue === 'Sarah Johnson';
                  return fieldValue === filter.value;
                case 'notEquals':
                  return fieldValue !== filter.value;
                case 'contains':
                  return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
                case 'startsWith':
                  return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
                default:
                  return true;
              }
            });
          });
        }

        if (listView.sortBy) {
          records = [...records].sort((a, b) => {
            const aValue = resolveFieldValue(a, listView.sortBy!.fieldApiName);
            const bValue = resolveFieldValue(b, listView.sortBy!.fieldApiName);
            if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
            return aValue < bValue ? 1 : -1;
          });
        }

        return { listView, records, totalRecords };
      }

      const allRecords = persistedClientRecords.get();
      let filtered: ListViewRecord[] = [...allRecords];
      if (listView.filters?.length) {
        listView.filters.forEach(filter => {
          filtered = filtered.filter(record => {
            const fieldValue = resolveFieldValue(record, filter.fieldApiName);
            switch (filter.operator) {
              case 'equals':
                if (filter.value === 'Current User') return fieldValue === 'Sarah Johnson';
                return fieldValue === filter.value;
              case 'notEquals':
                return fieldValue !== filter.value;
              case 'contains':
                return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
              case 'startsWith':
                return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
              default:
                return true;
            }
          });
        });
      }
      if (listView.sortBy) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = resolveFieldValue(a, listView.sortBy!.fieldApiName);
          const bValue = resolveFieldValue(b, listView.sortBy!.fieldApiName);
          if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
          return aValue < bValue ? 1 : -1;
        });
      }
      const totalRecords = filtered.length;
      const start = page * pageSize;
      const records = filtered.slice(start, start + pageSize);
      return { listView, records, totalRecords };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!listViewId,
    placeholderData: keepPreviousData, // keep previous page data (incl. totalRecords) while loading next page so pagination doesn't reset
  });
}

/**
 * Get default list view for clients
 */
export function useDefaultClientListView() {
  return useQuery({
    queryKey: ['defaultClientListView'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));

      const allViews = persistedClientListViews.get();
      const defaultView = allViews.find(lv => lv.isDefault);

      return defaultView || allViews[0];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Format datetime for display
 */
export function formatDateTime(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
  // Output format: "Nov 5, 2025, 10:00 AM"
}

/**
 * Format date for display
 */
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

/**
 * Update a client list view
 * Used by EditViewModal to persist changes
 */
export function useUpdateClientListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ListView> }) => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update in localStorage via mockDataManager
      const updated = persistedClientListViews.update(id, {
        ...updates,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) {
        throw new Error('List view not found');
      }

      return updated;
    },
    onSuccess: (data) => {
      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['clientListViews'] });
      queryClient.invalidateQueries({ queryKey: ['clientListViewData', data.id] });
      queryClient.invalidateQueries({ queryKey: ['defaultClientListView'] });
    },
  });
}
